import { useState } from 'react';
import { Loader2, Play, Link2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function StepBadge({ ok }) {
  if (ok) {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1 text-[10px]">
        <CheckCircle2 className="h-3 w-3" />
        OK
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1 text-[10px]">
      <XCircle className="h-3 w-3" />
      FAIL
    </Badge>
  );
}

const STEP_LABELS = {
  token_acquire: 'Token Acquire/Refresh',
  providers_mock_check: 'Mock Bank Check',
  fetch_accounts: 'Fetch Accounts',
};

function stepLabel(stepName) {
  if (STEP_LABELS[stepName]) return STEP_LABELS[stepName];
  if (stepName.startsWith('fetch_transactions_')) {
    const accId = stepName.replace('fetch_transactions_', '');
    return `Fetch Transactions (${accId})`;
  }
  return stepName;
}

export function OBConnectChainDryrunPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.obConnectChainDryrun();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const allOk = result?.steps?.length > 0 && result.steps.every(s => s.ok);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Link2 className="h-5 w-5 text-indigo-600" />
            OB Connect Chain
          </CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Run Connect Chain
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Exercises the TrueLayer data path: token refresh, providers check (uk-cs-mock for sandbox), accounts, balances, and transactions. Reports parsed shapes to prove the results[]/account_id/current parsing. Read-only.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-4">
          {/* Overall banner */}
          <div className={`border rounded-lg p-3 ${allOk ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center gap-2 text-sm font-medium">
              {allOk ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-green-700 dark:text-green-400">
                    Connect chain succeeded through all reachable steps
                  </span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="text-red-700 dark:text-red-400">
                    Connect chain stopped at a failing step (see below)
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Step results */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
              Steps
            </h4>
            <div className="space-y-2">
              {result.steps?.map((step, i) => (
                <div key={i} className="border rounded-lg p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    <StepBadge ok={step.ok} />
                    <span className="text-sm font-medium">{stepLabel(step.step)}</span>
                    {step.upstream_status && (
                      <span className="text-xs text-muted-foreground font-mono">
                        HTTP {step.upstream_status}
                      </span>
                    )}
                  </div>
                  {step.upstream_body && (
                    <pre className="mt-1 p-2 bg-muted rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
                      {typeof step.upstream_body === 'object'
                        ? JSON.stringify(step.upstream_body, null, 2)
                        : String(step.upstream_body)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </div>

          {result.checked_at && (
            <p className="text-[10px] text-muted-foreground text-right">
              Checked at {new Date(result.checked_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
